import type { CallSummaryEntry, EmitCtx, JourneyConfig, JourneyState } from './types';

export const ENABLE_PERF_METRICS_KEY = 'enablePerfMetrics';

/**
 * Enable with `localStorage.setItem('enablePerfMetrics', '1')` (reload), or
 * `?enablePerfMetrics=1` on the URL (persists the localStorage flag).
 */
export function isPerfMetricsEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    if (new URLSearchParams(window.location.search).get(ENABLE_PERF_METRICS_KEY) === '1') {
      window.localStorage.setItem(ENABLE_PERF_METRICS_KEY, '1');
      return true;
    }
    return window.localStorage.getItem(ENABLE_PERF_METRICS_KEY) === '1';
  } catch {
    return false;
  }
}

/** Only the slice of `JourneyRegistry` this module needs — avoids importing the class itself,
 * which would import back from `./core` and reintroduce the cycle `core.ts` is on the other end of. */
type DeltaComputable = { computeDeltas(id: string): Record<string, number | null> | null };

const journeysByName = new Map<string, DeltaComputable>();

export function registerJourney(name: string, registry: DeltaComputable): void {
  journeysByName.set(name, registry);
}

declare global {
  interface Window {
    __journeyDeltas?: (name: string, id: string) => Record<string, number | null> | null;
  }
}

if (typeof window !== 'undefined') {
  window.__journeyDeltas = (name: string, id: string) => journeysByName.get(name)?.computeDeltas(id) ?? null;
}

export function logMark<Mark extends string>(
  config: JourneyConfig<Mark>,
  ctx: Pick<EmitCtx<Mark>, 'id' | 'mark' | 'sinceStart' | 'marks'>
): void {
  if (!isPerfMetricsEnabled()) return;
  console.debug(`[${config.name}] ${ctx.mark ?? 'begin'}`, {
    name: config.name,
    id: ctx.id,
    sinceStartMs: ctx.sinceStart
  });
  // Journeys with no terminalMark (e.g. Signup — there's no always-last step, a user can land
  // straight on `password`) never reach logTerminalReport's console.table. Print a lightweight
  // marks-so-far table on every real mark instead, so there's still something to look at.
  if (!config.terminalMark && ctx.mark) {
    console.table(ctx.marks);
  }
}

export function logTerminalReport<Mark extends string>(
  config: JourneyConfig<Mark>,
  state: JourneyState<Mark>,
  ctx: EmitCtx<Mark>,
  callSummary?: CallSummaryEntry[]
): void {
  if (!isPerfMetricsEnabled()) return;

  const table: Record<string, string | number | null> = { mode: ctx.mode };
  for (const [name, ms] of Object.entries(ctx.deltas ?? {})) {
    table[name] = ms;
  }
  table.bottleneck = ctx.bottleneck ?? 'unknown';
  table.startToTerminalMs = ctx.sinceStart;

  console.info(`[${config.name}] journeyData`, { name: config.name, ...ctx, calls: state.calls });
  console.table(table);

  // One table per kind, not shared: `serverMs` only ever applies to WS, `networkMs` to GraphQL/REST —
  // a shared table would show an always-'—' column for whichever field doesn't apply to that row's
  // kind, and GraphQL/REST rolling up together would blur two different endpoints/transports.
  const wsSummary = callSummary?.filter((c) => c.kind === 'ws') ?? [];
  const graphqlSummary = callSummary?.filter((c) => c.kind === 'graphql') ?? [];
  const restSummary = callSummary?.filter((c) => c.kind === 'rest') ?? [];

  if (wsSummary.length) {
    console.table(
      wsSummary.map((c) => ({
        name: c.name,
        count: c.count,
        totalMs: c.totalMs,
        maxMs: c.maxMs,
        earliestStartedAt: c.earliestStartedAt,
        latestEndedAt: c.latestEndedAt,
        totalServerMs: c.totalServerMs ?? '—',
        maxServerMs: c.maxServerMs ?? '—'
      }))
    );
  }

  const networkRow = (c: CallSummaryEntry) => ({
    name: c.name,
    count: c.count,
    totalMs: c.totalMs,
    maxMs: c.maxMs,
    earliestStartedAt: c.earliestStartedAt,
    latestEndedAt: c.latestEndedAt,
    totalNetworkMs: c.totalNetworkMs ?? '—',
    maxNetworkMs: c.maxNetworkMs ?? '—',
    totalResourceDurationMs: c.totalResourceDurationMs ?? '—',
    maxResourceDurationMs: c.maxResourceDurationMs ?? '—'
  });

  if (graphqlSummary.length) console.table(graphqlSummary.map(networkRow));
  if (restSummary.length) console.table(restSummary.map(networkRow));

  if (ctx.assetLoadTime) {
    console.info(`[${config.name}] assetLoadTime`, ctx.assetLoadTime);
    if (ctx.assetLoadTime.slowest.length) console.table(ctx.assetLoadTime.slowest);
  }
}
